import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/atoms/buttons";

const dashboardCards = [
  {
    title: "Total Projects",
    value: "12",
    description: "Active projects",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-100",
    textColor: "text-blue-700",
    accent: "bg-blue-600",
    label: "PR",
  },
  {
    title: "Team Members",
    value: "8",
    description: "Collaborators",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-100",
    textColor: "text-violet-700",
    accent: "bg-violet-600",
    label: "TM",
  },
  {
    title: "Tasks Completed",
    value: "48",
    description: "This month",
    bgColor: "bg-green-50",
    borderColor: "border-green-100",
    textColor: "text-green-700",
    accent: "bg-green-600",
    label: "TC",
  },
  {
    title: "In Progress",
    value: "5",
    description: "Active tasks",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-100",
    textColor: "text-amber-700",
    accent: "bg-amber-500",
    label: "IP",
  },
];

const recentActivity = [
  { action: "Created new project", time: "2 hours ago" },
  { action: "Updated team settings", time: "5 hours ago" },
  { action: "Completed task #123", time: "1 day ago" },
  { action: "Invited 2 new members", time: "2 days ago" },
];

const quickActions = [
  {
    label: "New Project",
    className: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
  },
  {
    label: "Invite Member",
    className:
      "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100",
  },
  {
    label: "View Reports",
    className: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
  },
  {
    label: "Settings",
    className: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
  },
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "User";
  const initials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() ||
    "U";

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Civix workspace
            </p>
            <h1 className="mt-1 text-3xl font-bold text-slate-950 sm:text-4xl">
              Dashboard
            </h1>
            <p className="mt-2 text-slate-600">
              Welcome back,{" "}
              <span className="font-semibold text-slate-950">
                {user?.firstName || "User"}
              </span>
            </p>
          </div>
          <Button onClick={handleLogout} className="w-full sm:w-auto">
            Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <section className="mb-10 rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xl font-bold text-white">
              {initials}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-950">
                {displayName}
              </h2>
              <p className="text-slate-600">{user?.email || "No email found"}</p>
              <p className="mt-2 inline-flex rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
                Account active
              </p>
            </div>
          </div>
        </section>

        <section className="mb-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {dashboardCards.map((card) => (
            <article
              key={card.title}
              className={`${card.bgColor} ${card.borderColor} rounded-lg border p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm font-medium ${card.textColor}`}>
                    {card.title}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-slate-950">
                    {card.value}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    {card.description}
                  </p>
                </div>
                <span
                  className={`${card.accent} flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold text-white`}
                >
                  {card.label}
                </span>
              </div>
            </article>
          ))}
        </section>

        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="mb-6 text-2xl font-bold text-slate-950">
                Recent Activity
              </h2>
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div
                    key={`${activity.action}-${activity.time}`}
                    className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 transition hover:bg-slate-100 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <p className="font-medium text-slate-800">
                      {activity.action}
                    </p>
                    <p className="text-sm text-slate-500">{activity.time}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="mb-6 text-2xl font-bold text-slate-950">
              Quick Actions
            </h2>
            <div className="space-y-3">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  className={`w-full rounded-md border px-4 py-3 font-medium transition ${action.className}`}
                  type="button"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
